<script setup lang="ts">
import { computed } from "vue"
import { Hash, KanbanSquare, Volume2 } from "lucide-vue-next"
import { Button } from "~/components/ui/button"
import { useGroupsStore } from "~/stores/groups"
import { useHiddenChannelsStore } from "~/stores/hiddenChannels"
import { ChannelType } from "~/types/groups"

// Channels hidden on this device (stores/hiddenChannels.ts), grouped under their category's name like
// the channel list itself, each with «Показать» to put it back. One category (`categoryId`) — the
// category Drawer — or the whole group — the Drawer a long-press on the empty channel list opens.
const props = defineProps<{
  groupId: string
  categoryId?: string
}>()

const groupsStore = useGroupsStore()
const hiddenChannels = useHiddenChannelsStore()

const sections = computed(() =>
  (groupsStore.groupDetails[props.groupId]?.categories ?? [])
    .filter((category) => !props.categoryId || category.id === props.categoryId)
    .map((category) => ({
      id: category.id,
      name: category.name,
      channels: category.channels.filter((channel) => hiddenChannels.isHidden(props.groupId, channel.id)),
    }))
    .filter((section) => section.channels.length > 0))

const hiddenCount = computed(() => sections.value.reduce((sum, section) => sum + section.channels.length, 0))

function channelIcon(type: ChannelType) {
  if (type === ChannelType.Voice) return Volume2
  if (type === ChannelType.Board) return KanbanSquare
  return Hash
}

function show(channelId: string) {
  hiddenChannels.show(props.groupId, [channelId])
}

function showAll() {
  hiddenChannels.show(props.groupId, sections.value.flatMap((section) => section.channels.map((channel) => channel.id)))
}

defineExpose({ hiddenCount })
</script>

<template>
  <div v-if="sections.length" class="space-y-4" data-testid="hidden-channels-list">
    <section v-for="section in sections" :key="section.id" class="space-y-2">
      <div class="flex items-center justify-between gap-2 px-4">
        <h3 class="min-w-0 truncate text-xs font-semibold uppercase text-muted-foreground">
          {{ categoryId ? 'Скрытые каналы' : section.name }}
        </h3>
        <button
            v-if="section === sections[0] && hiddenCount > 1"
            type="button"
            class="shrink-0 text-xs font-medium text-primary active:opacity-70"
            data-testid="hidden-channels-show-all"
            @click="showAll"
        >Показать все</button>
      </div>
      <div class="mx-4 divide-y divide-border/60 overflow-hidden rounded-2xl border bg-muted/40">
        <div
            v-for="channel in section.channels"
            :key="channel.id"
            class="flex min-h-12 w-full items-center gap-3 px-4 py-2"
            data-testid="hidden-channel-row"
        >
          <span class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
            <component :is="channelIcon(channel.type)" class="size-5"/>
          </span>
          <span class="min-w-0 flex-1 truncate text-[15px] font-medium">{{ channel.name }}</span>
          <Button size="sm" variant="secondary" class="shrink-0 rounded-full" @click="show(channel.id)">Показать</Button>
        </div>
      </div>
    </section>
  </div>
  <p v-else-if="!categoryId" class="px-4 py-6 text-center text-sm text-muted-foreground">
    Скрытых каналов нет. Скрыть канал можно долгим тапом по нему.
  </p>
</template>
